import { z } from 'zod';
import { EmailSchema, PhoneSchema, PasswordSchema } from './common.js';
// =============================================================================
// Auth Schemas - Dual-Method Authentication (Phone SMS + Email OTP)
// =============================================================================
// Per Deep Architect Review:
// - Separate auth_challenges collection for verification tokens
// - Email OTP (not magic links) for cross-device support
// - Stateless JWE tokens for signup handoff
// - Rate limiting per identifier and IP
// =============================================================================
// =============================================================================
// Enums
// =============================================================================
export const IdentifierTypeEnum = z.enum(['phone', 'email']);
export const PrimaryContactMethodEnum = z.enum(['email', 'phone']);
export const UserTypeEnum = z.enum(['AGENT', 'TRANSACTION_COORDINATOR', 'ADMIN']);
export const AuthFlowTypeEnum = z.enum(['login', 'signup']);
// =============================================================================
// Base Schemas
// =============================================================================
/**
 * Identifier schema - supports both phone (E.164) and email
 * Used for flexible authentication
 */
export const IdentifierSchema = z.union([
    PhoneSchema,
    EmailSchema,
]);
/**
 * Verification code - 6 digits
 */
export const VerificationCodeSchema = z
    .string()
    .length(6, 'Code must be 6 digits')
    .regex(/^\d{6}$/, 'Code must contain only digits');
// =============================================================================
// Auth Challenge Schemas (for auth_challenges collection)
// =============================================================================
/**
 * Auth Challenge Document - ephemeral verification tokens
 * Solves the "New User Paradox" - tokens can exist before user documents
 */
export const AuthChallengeSchema = z.object({
    _id: z.string(),
    identifier: z.string(), // Normalized phone (+1...) or email (lowercase)
    identifierType: IdentifierTypeEnum,
    tokenHash: z.string(), // Argon2 hash (NEVER plaintext)
    attempts: z.number().min(0).default(0),
    ipAddress: z.string(),
    userAgent: z.string().optional(),
    expiresAt: z.coerce.date(),
    createdAt: z.coerce.date(),
});
// =============================================================================
// Rate Limit Schemas (for auth_rate_limits collection)
// =============================================================================
/**
 * Rate Limit Type - what is being rate limited
 */
export const RateLimitTypeEnum = z.enum(['sms', 'email', 'ip']);
/**
 * Rate Limit Document - prevents toll fraud and abuse
 */
export const RateLimitSchema = z.object({
    _id: z.string(),
    key: z.string(), // Format: "sms:{phone}" or "email:{email}" or "ip:{ip}"
    type: RateLimitTypeEnum,
    count: z.number().min(0).default(1),
    windowStart: z.coerce.date(),
    expiresAt: z.coerce.date(),
});
/**
 * Rate Limit Check Response
 */
export const RateLimitCheckSchema = z.object({
    allowed: z.boolean(),
    remaining: z.number(),
    resetAt: z.coerce.date(),
    count: z.number(),
});
// =============================================================================
// Request Schemas
// =============================================================================
/**
 * Login Request - Step 1: Send verification code
 * Supports both phone (SMS) and email (OTP)
 */
export const SendCodeRequestSchema = z.object({
    identifier: IdentifierSchema,
    identifierType: IdentifierTypeEnum,
});
/**
 * Verify Code Request - Step 2: Validate the code
 * Returns JWT for existing users, signup token for new users
 */
export const VerifyCodeRequestSchema = z.object({
    identifier: IdentifierSchema,
    identifierType: IdentifierTypeEnum,
    code: VerificationCodeSchema,
});
/**
 * Password Login Request - email + password (primary login method, v1 2026-09)
 */
export const PasswordLoginRequestSchema = z.object({
    email: EmailSchema,
    password: z.string().min(1, 'Password is required').max(128),
});
/**
 * MFA Verify Request - second step after password login when MFA is enabled
 */
export const MfaVerifyRequestSchema = z.object({
    email: EmailSchema,
    code: VerificationCodeSchema,
});
/**
 * Forgot Password Request - always responds 200 (anti-enumeration)
 */
export const ForgotPasswordRequestSchema = z.object({
    email: EmailSchema,
});
/**
 * Reset Password Request - single-use emailed token
 */
export const ResetPasswordRequestSchema = z.object({
    email: EmailSchema,
    token: z.string().min(1, 'Reset token is required'),
    newPassword: PasswordSchema,
});
/**
 * Change/Set Password Request (authenticated).
 * currentPassword is required iff the account already has a password;
 * first-time set from an OTP-authenticated session omits it.
 */
export const ChangePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1).max(128).optional(),
    newPassword: PasswordSchema,
});
/**
 * MFA toggle (authenticated). Enabling requires a phone on file.
 */
export const SetMfaRequestSchema = z.object({
    enabled: z.boolean(),
});
/**
 * Signup Request - Step 3: Complete registration for new users
 * Requires valid signup token from verify step
 */
export const SignupRequestSchema = z.object({
    signupToken: z.string().min(1, 'Signup token is required'),
    firstName: z.string().min(1, 'First name is required').max(50),
    lastName: z.string().min(1, 'Last name is required').max(50),
    // Password auth v1 (2026-09): required on the main signup path.
    // Share-link signup (ShareSignupRequestSchema) deliberately does NOT
    // require a password — it stays frictionless.
    password: PasswordSchema,
    // Optional: add secondary contact method
    email: EmailSchema.optional(),
    phone: PhoneSchema.optional(),
    // Professional info
    companyName: z.string().max(100).optional(),
    licenseNumber: z.string().max(50).optional(),
    // User type (Agent or TC) - defaults to AGENT on backend
    userType: UserTypeEnum.exclude(['ADMIN']).optional(),
    // Terms of Service acceptance - must be exactly true
    termsAccepted: z.literal(true, {
        errorMap: () => ({ message: 'You must accept the Terms of Service' }),
    }),
});
/**
 * Share-Link Signup Request - Simplified registration from share link context
 * Uses JWE signup token from verify step but relaxes validation:
 * - No companyName or licenseNumber required (fill later in Settings)
 * - No secondary email/phone (verified email IS the primary)
 * - userType is required (user explicitly chose Agent or TC in Gatekeeper)
 */
export const ShareSignupRequestSchema = z.object({
    signupToken: z.string().min(1, 'Signup token is required'),
    firstName: z.string().min(1, 'First name is required').max(50),
    lastName: z.string().min(1, 'Last name is required').max(50),
    phone: PhoneSchema.optional(),
    userType: UserTypeEnum.exclude(['ADMIN']),
    shareId: z.string().min(1, 'Share ID is required'),
    termsAccepted: z.literal(true, {
        errorMap: () => ({ message: 'You must accept the Terms of Service' }),
    }),
});
/**
 * Check User Request - Lightweight email existence check for Gatekeeper
 * Used to determine whether to show new-user or existing-user OTP screen
 */
export const CheckUserRequestSchema = z.object({
    email: EmailSchema,
});
/**
 * Logout Request - optional, allows logout from specific device
 */
export const LogoutRequestSchema = z.object({
    allDevices: z.boolean().default(false), // Reserved for future multi-session support
});
// =============================================================================
// Response Schemas
// =============================================================================
/**
 * Send Code Response - confirms code was sent
 */
export const SendCodeResponseSchema = z.object({
    success: z.literal(true),
    message: z.string(),
    // For display purposes (masked)
    maskedIdentifier: z.string(),
    // Rate limit info
    codeExpiresAt: z.coerce.date(),
    canResendAt: z.coerce.date().optional(),
});
/**
 * Verify Code Response - different based on user existence
 */
export const VerifyCodeResponseSchema = z.discriminatedUnion('flow', [
    // Existing user - return session
    z.object({
        flow: z.literal('login'),
        success: z.literal(true),
        user: z.object({
            _id: z.string(),
            firstName: z.string(),
            lastName: z.string(),
            email: EmailSchema.optional(),
            phone: PhoneSchema.optional(),
            primaryContactMethod: PrimaryContactMethodEnum,
            userType: UserTypeEnum,
        }),
        // Session info
        sessionExpiresAt: z.coerce.date(),
    }),
    // New user - return signup token
    z.object({
        flow: z.literal('signup'),
        success: z.literal(true),
        // JWE token containing verified identifier (stateless, cross-device)
        signupToken: z.string(),
        // Verified identifier for display
        verifiedIdentifier: z.string(),
        verifiedIdentifierType: IdentifierTypeEnum,
        // Token validity
        tokenExpiresAt: z.coerce.date(),
    }),
]);
/**
 * Password Login Response — either a session (same user shape as the OTP
 * login flow) or an MFA challenge to complete via /auth/verify-mfa.
 */
export const PasswordLoginResponseSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('ok'),
        success: z.literal(true),
        user: z.object({
            _id: z.string(),
            firstName: z.string(),
            lastName: z.string(),
            email: EmailSchema.optional(),
            phone: PhoneSchema.optional(),
            primaryContactMethod: PrimaryContactMethodEnum,
            userType: UserTypeEnum,
        }),
        sessionExpiresAt: z.coerce.date(),
    }),
    z.object({
        status: z.literal('mfa_required'),
        success: z.literal(true),
        maskedPhone: z.string(),
        codeExpiresAt: z.coerce.date(),
    }),
]);
/**
 * Signup Response - new user created
 */
export const SignupResponseSchema = z.object({
    success: z.literal(true),
    user: z.object({
        _id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: EmailSchema.optional(),
        phone: PhoneSchema.optional(),
        primaryContactMethod: PrimaryContactMethodEnum,
        userType: UserTypeEnum,
    }),
    organization: z.object({
        _id: z.string(),
        name: z.string(),
        slug: z.string(),
    }),
    sessionExpiresAt: z.coerce.date(),
});
/**
 * Auth Error Response - standardized error format
 */
export const AuthErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.object({
        code: z.enum([
            'INVALID_IDENTIFIER',
            'RATE_LIMITED',
            'INVALID_CODE',
            'EXPIRED_CODE',
            'MAX_ATTEMPTS_EXCEEDED',
            'INVALID_SIGNUP_TOKEN',
            'EXPIRED_SIGNUP_TOKEN',
            'USER_ALREADY_EXISTS',
            'USER_INACTIVE',
            'SESSION_EXPIRED',
            'UNAUTHORIZED',
            'INTERNAL_ERROR',
            // Password auth v1 (2026-09)
            'INVALID_CREDENTIALS',
            'ACCOUNT_LOCKED',
            'WEAK_PASSWORD',
            'NO_PASSWORD_SET',
            'INVALID_RESET_TOKEN',
            'EXPIRED_RESET_TOKEN',
            'MFA_REQUIRED',
            'INVALID_MFA_CODE',
        ]),
        message: z.string(),
        // Additional context
        retryAfter: z.number().optional(), // Seconds until retry allowed
        attemptsRemaining: z.number().optional(), // For code verification
    }),
});
// =============================================================================
// Session Schemas
// =============================================================================
/**
 * Session data stored in express-session
 */
export const AuthSessionSchema = z.object({
    userId: z.string(),
    activeOrganizationId: z.string(),
    membershipId: z.string(),
    role: z.enum(['OWNER', 'ADMIN', 'MEMBER']),
});
/**
 * Authenticated User Response (/auth/me)
 */
export const AuthMeResponseSchema = z.object({
    user: z.object({
        _id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: EmailSchema.optional(),
        phone: PhoneSchema.optional(),
        emailVerified: z.boolean(),
        phoneVerified: z.boolean(),
        primaryContactMethod: PrimaryContactMethodEnum,
        userType: UserTypeEnum,
        avatarUrl: z.string().optional(),
        companyName: z.string().optional(),
        licenseNumber: z.string().optional(),
        isActive: z.boolean(),
        lastLoggedIn: z.coerce.date().optional(),
        hasDocuSignConnected: z.boolean(),
        notificationPrefs: z
            .object({
            packageActivity: z.boolean(),
        })
            .optional(),
        createdAt: z.coerce.date(),
        updatedAt: z.coerce.date(),
    }),
    organization: z.object({
        _id: z.string(),
        name: z.string(),
        slug: z.string(),
        type: z.string(),
        role: z.enum(['OWNER', 'ADMIN', 'MEMBER']),
    }),
});
/**
 * Input for PATCH /auth/me/notification-prefs.
 * All fields optional so callers can update one toggle at a time.
 */
export const UpdateNotificationPrefsInputSchema = z.object({
    packageActivity: z.boolean().optional(),
});
export const NotificationPrefsResponseSchema = z.object({
    packageActivity: z.boolean(),
});
// =============================================================================
// JWE Token Payload Schemas (for stateless signup handoff)
// =============================================================================
/**
 * Signup Token Payload - encrypted in JWE
 * Contains verified identifier for cross-device signup completion
 */
export const SignupTokenPayloadSchema = z.object({
    // Verified identifier
    identifier: z.string(),
    identifierType: IdentifierTypeEnum,
    // Security context
    ipAddress: z.string(),
    verifiedAt: z.coerce.date(),
    // Token metadata
    iat: z.number(), // Issued at (Unix timestamp)
    exp: z.number(), // Expires at (Unix timestamp)
});
// =============================================================================
// Utility Functions
// =============================================================================
/**
 * Determine identifier type from value
 */
export function detectIdentifierType(identifier) {
    // E.164 format starts with +
    if (identifier.startsWith('+') && /^\+[1-9]\d{1,14}$/.test(identifier)) {
        return 'phone';
    }
    // Otherwise assume email
    return 'email';
}
/**
 * Normalize identifier for storage/comparison
 * - Phone: Ensure E.164 format
 * - Email: Lowercase
 */
export function normalizeIdentifier(identifier, type) {
    if (type === 'phone') {
        // Already in E.164 if it passed validation
        return identifier.trim();
    }
    // Email: lowercase and trim
    return identifier.toLowerCase().trim();
}
/**
 * Mask identifier for display
 * - Phone: +1••••••1234
 * - Email: j•••@example.com
 */
export function maskIdentifier(identifier, type) {
    if (type === 'phone') {
        // Show country code and last 4 digits
        const len = identifier.length;
        if (len <= 4)
            return identifier;
        return identifier.slice(0, 2) + '••••••' + identifier.slice(-4);
    }
    // Email: show first char and domain
    const [local, domain] = identifier.split('@');
    if (!local || !domain)
        return identifier;
    return local[0] + '•••@' + domain;
}
//# sourceMappingURL=auth.js.map